<template>
  <InputText
    allowedChars="0-9\."
    :icon="icon"
    :identifier="identifier"
    :label="label"
    :initialValue="initialValue"
    :validations="computedValidations"
    :maxLength="15"
    :transformationHook="applyTransform"
    :informationTooltipContent="informationTooltipContent"
    :forcedValidations="forcedValidations"
    @onChange="data => $emit('onChange', data)"
    @onInput="data => $emit('onInput', data)"
  />
</template>

<script>
import { InputText } from "@/components";
/**
 * @description Contrôle pour adresse IP
 * @author Samuel Dube
 * @author Alexandre Gallant
 * @vue-prop {String} initialValue - Valeur initiale de la saisie
 * @vue-prop {String} identifier - Identifiant de l'input
 * @vue-prop {String} label - Étiquette affichée pour l'input
 * @vue-prop {String} icon - Icône affichée pour l'input
 * @vue-prop {String} informationTooltipContent - Le message à afficher pour le tooltip d'information (null pour qu'il n'y en est pas)
 * @vue-prop {Number} forcedValidations - Nombre de validations forcées (les validations sont exécutées lorsque la valeur change)
 * @vue-computed {Array} computedValidations - Retourne toutes les validations à effectuer
 */
export default {
  name: "InputIpAddress",
  components: {
    InputText
  },
  props: {
    initialValue: {
      type: String,
      required: false,
      default: ""
    },
    identifier: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: false,
      default: "wifi icon"
    },
    informationTooltipContent: {
      type: String,
      required: false,
      default: null
    },
    forcedValidations: {
      type: Number,
      required: false,
      default: 0
    }
  },
  computed: {
    computedValidations() {
      return [
        {
          error: this.$i18n.t("errors.empty", { name: this.label }),
          exec: v => v !== ""
        },
        {
          error: this.$i18n.t("errors.notValid", { name: this.label }),
          exec: v => new RegExp(/^([0-9]{1,3}\.){3}[0-9]{1,3}$/).test(v)
        }
      ];
    }
  },
  methods: {
    /**
     * @description Méthode appliquant des transformations à la valeur
     * afin de le convertir dans un format ip
     * @param {String} value La nouvelle valeur du contrôle
     * @param {String} oldValue L'ancienne valeur du contrôle
     * @param {String} input Le ou les caractères à ajouter
     * @returns {String} La valeur finale à donner au input
     */
    applyTransform(value, oldValue, input) {
      if (value == "") {
        return value;
      }

      // On revient en arrière si le format est invalide
      if (!__isFormatValid(value)) {
        return oldValue;
      }

      // On reformate le contenu du contrôle (changement des valeurs plus grandes que 255, et ajout de points si nécessaire)
      return __format(value, input);

      /**
       * @description Formate le contenu du contrôle
       * @param {String} value La valeur à formater
       * @param {String} input Le ou les caractères à ajouter
       * @returns {String} La valeur formatée
       */
      function __format(value, input) {
        var segments = __segmentIp(value);

        for (const index in __segmentIp(value)) {
          if (segments[index] > 255) {
            segments[index] = "255";
          } else if (segments[index][0] == "0" && segments[index] != "0") {
            segments[index] = segments[index].substring(1);
          }
        }

        value = __writeIp(segments, value);

        // Ajout de «.»
        if (
          segments.length < 4 &&
          ((segments[segments.length - 1] == "0" &&
            value[value.length - 1] == "0" &&
            input != null) ||
            (segments[segments.length - 1].length == 3 &&
              value[value.length - 1] != "." &&
              input != null))
        ) {
          value += ".";
        }
        return value;
      }

      /**
       * @description Change le contenu du contrôle selon les paramètres données
       * @param {Array} segments Les segments de l'adresse ip à réécrire
       * @param {String} value La valeur actuel de l'input
       * @returns {String} La valeur réécrite
       */
      function __writeIp(segments, value) {
        var newValue = "";
        for (const index in segments) {
          const segment = segments[index];
          newValue += segment;
          if (index < segments.length - 1 || value[value.length - 1] == ".") {
            newValue += ".";
          }
        }
        return newValue;
      }

      /**
       * @description Vérifie si l'adresse donnée est valide
       * @param {Array} value L'adresse à vérifier
       * @returns {Boolean} True si c'est valide, false sinon
       */
      function __isFormatValid(value) {
        const regex = [
          /^([0-9]{1,3}\.){3}[0-9]{1,3}$/, //  ###.###.###.###
          /^([0-9]{1,3}\.){3}$/, //            ###.###.###.
          /^([0-9]{1,3}\.){2}[0-9]{1,3}$/, //  ###.###.###
          /^([0-9]{1,3}\.){2}$/, //            ###.###.
          /^[0-9]{1,3}\.[0-9]{1,3}$/, //       ###.###
          /^[0-9]{1,3}\.$/, //                 ###.
          /^[0-9]{1,3}$/, //                   ###
          /^$/
        ];

        for (const index in regex) {
          if (regex[index].test(value)) {
            return true;
          }
        }
        return false;
      }
      /**
       * @description Segmente l'adresse fournie et retourne les segments
       * @param {String} value L'adresse ip à segmenter
       * @returns {Array} L'adresse segmentée
       */
      function __segmentIp(value) {
        const regex = new RegExp(/\.?[0-9]{1,3}\.?/g);
        var segments = [],
          result;
        while ((result = regex.exec(value))) {
          segments.push(result[0].replace(/^\.+|\.+$/g, ""));
        }
        return segments;
      }
    }
  }
};
</script>
